import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { formatDate } from '@/lib/format';
import { leadStatus, orderStatus } from '@/theme/status';
import type { LeadCard, RecalculoLeadResultado } from '@/types/crm';
import {
  channelLabel,
  contactLabel,
  describeAutomaticClassification,
  describeHistorialContext,
  describeHistorialEntry,
  describeLeadAnalysis,
  describeLeadMotivo,
  describeLeadOrigin,
  describeLeadSignals,
  describeLeadStats,
  describeMessage,
  describeRecalculoLead,
  describeUltimaOrden,
  formatNumber,
  formatRelativeTime,
  originLabel,
} from './crm-format';
import { useLead, useRecalculateLead } from './use-crm';

/**
 * La ficha de un lead (`GET .../crm/leads/:conversationId`): el "por qué" del kanban.
 *
 * El backend devuelve, además de la tarjeta, todo lo que hace falta para auditar el estado sin
 * abrir el chat: las SEÑALES que encontró el clasificador en los mensajes entrantes, qué diría el
 * clasificador HOY (aunque el lead esté reclasificado a mano), el historial de estados con quién y
 * por qué, las órdenes del contacto y los últimos 20 mensajes.
 *
 * Las dos escrituras del CRM se pueden disparar desde acá:
 *  - «Volver a clasificar automáticamente» (`POST .../recalcular`, cualquier miembro): deshace la
 *    reclasificación manual y responde qué dijo el análisis — la pantalla informa con eso, sin
 *    volver a pedir el detalle;
 *  - «Reclasificar a mano» (se delega a `LeadStatusDialog` con `onStatus`).
 *
 * Es un diálogo que se lee de arriba a abajo, así que `size="lg"`.
 */

export interface LeadDetailDialogProps {
  open: boolean;
  onClose: () => void;
  /** Conversación del lead (`null` = cerrado; sin id no se pide nada al backend). */
  conversationId: string | null;
  /** Abre la reclasificación manual (el lead se pasa tal como llegó del endpoint). */
  onStatus: (lead: LeadCard) => void;
}

export function LeadDetailDialog({
  open,
  onClose,
  conversationId,
  onStatus,
}: LeadDetailDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const detalle = useLead(tenantId, open ? conversationId : null);
  const recalculate = useRecalculateLead(tenantId);

  const [recalcResult, setRecalcResult] = useState<RecalculoLeadResultado | null>(null);
  const [recalcError, setRecalcError] = useState<string | null>(null);

  /** Cada apertura arranca sin el resultado del recálculo anterior. */
  useEffect(() => {
    if (!open) return;

    setRecalcResult(null);
    setRecalcError(null);
  }, [open, conversationId]);

  async function handleRecalculate() {
    if (!conversationId) return;

    setRecalcResult(null);
    setRecalcError(null);

    try {
      setRecalcResult(await recalculate.mutateAsync(conversationId));
    } catch (error) {
      setRecalcError(getErrorMessage(error));
    }
  }

  /** El `LeadCard` que espera el diálogo de reclasificación (el detalle lo extiende). */
  const lead = detalle.data ?? null;
  const meta = lead ? leadStatus[lead.estadoLead] : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ficha del lead"
      description={
        lead ? `${contactLabel(lead)} · ${channelLabel[lead.channel]}` : 'Cargando el lead…'
      }
      size="lg"
      footer={
        lead ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cerrar
            </Button>
            <Button onClick={() => onStatus(lead)}>Reclasificar a mano</Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        )
      }
    >
      <div className="space-y-5">
        {detalle.isPending ? <PageLoader label="Cargando el lead…" /> : null}

        {detalle.isError ? <Alert>{getErrorMessage(detalle.error)}</Alert> : null}

        {lead && meta ? (
          <>
            <section className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge label={meta.label} tone={meta.tone} />
                <Badge tone={lead.estadoLeadOrigen === 'MANUAL' ? 'brand' : 'neutral'}>
                  {originLabel[lead.estadoLeadOrigen]}
                </Badge>
                {lead.requiereAtencion ? (
                  <Badge tone="warning" dot>
                    Requiere atención humana
                  </Badge>
                ) : (
                  <Badge tone="success" dot>
                    Atendido por el bot
                  </Badge>
                )}
                {lead.adId || lead.campaignId ? (
                  <Badge tone="info">
                    {lead.campaignId ? `Campaña ${lead.campaignId}` : `Anuncio ${lead.adId}`}
                  </Badge>
                ) : null}
              </div>

              <dl className="grid gap-3 text-caption sm:grid-cols-2">
                <div>
                  <dt className="text-micro font-semibold tracking-wide text-ink-faint uppercase">
                    Por qué está ahí
                  </dt>
                  <dd className="mt-0.5 text-ink-soft">
                    {describeLeadMotivo(lead.estadoLeadMotivo)}
                  </dd>
                </div>

                <div>
                  <dt className="text-micro font-semibold tracking-wide text-ink-faint uppercase">
                    Estado desde
                  </dt>
                  <dd className="mt-0.5 text-ink-soft">{describeLeadOrigin(lead)}</dd>
                </div>

                <div>
                  <dt className="text-micro font-semibold tracking-wide text-ink-faint uppercase">
                    Conversación
                  </dt>
                  <dd className="mt-0.5 text-ink-soft">{describeLeadStats(lead)}</dd>
                </div>

                <div>
                  <dt className="text-micro font-semibold tracking-wide text-ink-faint uppercase">
                    Última actividad
                  </dt>
                  <dd className="mt-0.5 text-ink-soft">
                    {formatRelativeTime(lead.lastMessageAt ?? lead.createdAt)}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="space-y-2 rounded-panel border border-line px-3.5 py-3">
              <h3 className="text-caption font-semibold text-ink">Qué vio el clasificador</h3>
              <p className="text-caption text-ink-soft">{describeLeadSignals(lead.senales)}</p>

              <ul className="list-disc space-y-0.5 pl-4 text-caption text-ink-muted">
                {describeLeadAnalysis(lead.detalle).map((linea) => (
                  <li key={linea}>{linea}</li>
                ))}
              </ul>

              <p className="text-micro text-ink-faint">
                {describeAutomaticClassification(lead)}
                {lead.estadoLeadOrigen === 'MANUAL'
                  ? ' Este lead está reclasificado a mano, así que el estado que ves no es el del clasificador.'
                  : ''}
              </p>

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={recalculate.isPending}
                  onClick={handleRecalculate}
                >
                  Volver a clasificar automáticamente
                </Button>

                <span className="text-micro text-ink-faint">
                  Borra la decisión manual y deja el estado que calcula el clasificador hoy.
                </span>
              </div>

              {recalcResult ? (
                <Alert tone={recalcResult.actualizado ? 'success' : 'neutral'}>
                  {describeRecalculoLead(recalcResult)}
                </Alert>
              ) : null}

              {recalcError ? <Alert>{recalcError}</Alert> : null}
            </section>

            <section className="space-y-2">
              <h3 className="text-caption font-semibold text-ink">Órdenes del contacto</h3>

              {lead.ordenes.length === 0 ? (
                <p className="text-caption text-ink-muted">
                  Todavía no tiene órdenes: el lead sigue en la parte de arriba del embudo.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {lead.ordenes.map((orden) => (
                    <li
                      key={orden.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-panel border border-line px-3 py-2 text-caption"
                    >
                      <span className="text-ink-soft">
                        {`${orden.clienteNombre} · ${orderStatus[orden.estado].label}`}
                      </span>
                      <span className="text-ink-muted tabular">
                        {`${formatNumber(orden.total)} · ${formatDate(orden.createdAt)}`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <p className="text-micro text-ink-faint">{describeUltimaOrden(lead.ultimaOrden)}</p>
            </section>

            <section className="space-y-2">
              <h3 className="text-caption font-semibold text-ink">
                {`Historial de estados (${formatNumber(lead.historial.length)})`}
              </h3>

              {lead.historial.length === 0 ? (
                <p className="text-caption text-ink-muted">
                  Sin movimientos registrados: el lead nació en «{meta.label}» y no se ha movido.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {lead.historial.map((entry) => (
                    <li key={entry.id} className="rounded-panel border border-line px-3 py-2">
                      <p className="flex flex-wrap items-center justify-between gap-2 text-caption text-ink-soft">
                        <span className="font-medium">{describeHistorialEntry(entry)}</span>
                        <span className="text-micro text-ink-faint">
                          {formatRelativeTime(entry.createdAt)}
                        </span>
                      </p>
                      <p className="mt-0.5 text-micro text-ink-muted">
                        {describeHistorialContext(entry)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="text-caption font-semibold text-ink">
                {`Últimos mensajes (${formatNumber(lead.mensajesRecientes.length)})`}
              </h3>

              {lead.mensajesRecientes.length === 0 ? (
                <p className="text-caption text-ink-muted">
                  Sin mensajes entrantes todavía: el clasificador no tiene nada que leer para este
                  lead, así que lo deja en «Frío».
                </p>
              ) : (
                <ul className="scroll-thin max-h-56 space-y-1.5 overflow-y-auto">
                  {lead.mensajesRecientes.map((mensaje) => (
                    <li
                      key={mensaje.id}
                      className="rounded-panel bg-surface-muted/60 px-3 py-2 text-caption text-ink-soft"
                    >
                      {describeMessage(mensaje)}
                      <span className="mt-0.5 block text-micro text-ink-faint">
                        {formatDate(mensaje.createdAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
